import { Dispatch, SetStateAction, useCallback, useState, useRef, useContext } from "react"

// ** Third Party Components
import axios from "axios"
import * as XLSX from "xlsx"
import { parseCookies } from "nookies"
import { Check, Upload, Download } from "lucide-react"

// ** Theme Components
import { Button } from "@pma/components/common/button"
import {
  Dialog,
  DialogClose,
  DialogContent,
  DialogDescription,
  DialogFooter,
  DialogHeader,
  DialogTitle,
} from "@pma/components/common/dialog"

// ** Helpers
import { cn } from "@pma/utils/helpers"

// ** Constants
import { ACCESS_TOKEN } from "@pma/utils/constants"

// ** Routes
import { SERVER_URL, TASKS_IMPORT_TO_PROJECT } from "@pma/utils/api-routes"

// ** Context
import { GroupedTasksContext } from "./context/GroupedTasksProvider"

// ** Hooks
import { toast } from "@pma/hooks/use-toast"
import useTranslator from "@pma/hooks/use-translator"

interface ImportTaskDialogProps {
  importTaskDialog: boolean
  setImportTaskDialog: Dispatch<SetStateAction<boolean>>
  projectId: string
  onSuccess?: () => void
}

const REQUIRED_HEADERS: Record<string, string> = {
  title: "כותרת",
  description: "תיאור",
  status: "סטטוס",
  priority: "דחיפות",
  assigned: "הצמדה",
  start: "משך מ",
  finish: "משך ל",
}

const TEMPLATE_URLS = {
  en: "https://xk6s-eqwg-s8by.f2.xano.io/vault/qzkYj2ia/K1u8QUFXiBHhmy3cdMwc0X7112o/7mpS-w../import+tasks+en.xlsx",
  he: "https://xk6s-eqwg-s8by.f2.xano.io/vault/qzkYj2ia/fHqe3EDOKZMOPUUPvMctYtExg6I/O2SF1Q../import+tasks+he.xlsx",
}

const ImportTaskDialog = ({
  projectId,
  onSuccess,
  importTaskDialog,
  setImportTaskDialog,
}: ImportTaskDialogProps) => {
  const { translate, locale } = useTranslator()
  const { taskStatus, taskPrioritiesList } = useContext(GroupedTasksContext) as {
    taskStatus: any[]
    taskPrioritiesList: any[]
  }

  const accessToken = parseCookies()[ACCESS_TOKEN]
  const isHebrew = locale === "he"
  const fileInputRef = useRef<HTMLInputElement>(null)

  const [isLoading, setIsLoading] = useState(false)
  const [file, setFile] = useState<File | null>(null)
  const [isDragging, setIsDragging] = useState(false)
  const [isValidating, setIsValidating] = useState(false)
  const [validRowCount, setValidRowCount] = useState<number | null>(null)

  const showError = useCallback(
    (message: string) => {
      toast({
        title: translate("Error"),
        description: message,
        variant: "error",
      })
    },
    [translate],
  )

  const handleClose = useCallback(() => {
    setFile(null)
    setValidRowCount(null)
    setImportTaskDialog(false)
  }, [setImportTaskDialog])

  const isValidFileType = (file: File): boolean => {
    const ext = "." + file.name.split(".").pop()?.toLowerCase()
    return [".xls", ".xlsx", ".csv"].includes(ext)
  }

  const detectLanguage = (headers: string[]): "en" | "he" | null => {
    const normalizedHeaders = headers.map((h) => h.trim().toLowerCase())
    const enHeaders = Object.keys(REQUIRED_HEADERS).map((h) => h.toLowerCase())
    const heHeaders = Object.values(REQUIRED_HEADERS).map((h) => h.trim())

    if (enHeaders.every((h) => normalizedHeaders.includes(h))) return "en"
    if (heHeaders.every((h) => headers.map((fh) => fh.trim()).includes(h))) return "he"
    return null
  }

  const getHeaderKey = (header: string, lang: "en" | "he"): string | null => {
    const normalized = header.trim().toLowerCase()
    if (lang === "en")
      return Object.keys(REQUIRED_HEADERS).find((k) => k.toLowerCase() === normalized) || null
    const entry = Object.entries(REQUIRED_HEADERS).find(([_, v]) => v.trim() === header.trim())
    return entry ? entry[0] : null
  }

  const parseDate = (value: any): Date | null => {
    if (!value) return null
    if (typeof value === "number") {
      if (value > 0 && value < 100000)
        return new Date(new Date(1899, 11, 30).getTime() + value * 86400000)
      if (value > 0 && value < 4102444800) return new Date(value * 1000)
      if (value > 0) return new Date(value)
    }
    if (typeof value === "string") {
      const trimmed = value.trim()
      if (/^\d+$/.test(trimmed)) {
        const num = parseInt(trimmed, 10)
        return new Date(num < 4102444800 ? num * 1000 : num)
      }
      const parsed = Date.parse(trimmed)
      if (!isNaN(parsed)) return new Date(parsed)
    }
    return null
  }

  const isValidTimestamp = (value: any): boolean => {
    if (value === null || value === undefined || value === "") return true
    return parseDate(value) !== null
  }

  const parseFileData = async (
    file: File,
  ): Promise<{ headers: string[]; rows: Record<string, any>[] }> => {
    return new Promise((resolve, reject) => {
      const reader = new FileReader()
      reader.onload = (e) => {
        try {
          const data = e.target?.result
          if (file.name.endsWith(".csv")) {
            const lines = (data as string).split(/\r?\n/).filter((l) => l.trim())
            const headers = lines[0].split(",").map((h) => h.trim().replace(/^["']|["']$/g, ""))
            const rows = lines.slice(1).map((line) => {
              const values = line.split(",").map((v) => v.trim().replace(/^["']|["']$/g, ""))
              return headers.reduce((acc, h, i) => ({ ...acc, [h]: values[i] || "" }), {})
            })
            resolve({ headers, rows })
          } else {
            const workbook = XLSX.read(data, { type: "binary" })
            const sheet = workbook.Sheets[workbook.SheetNames[0]]
            const rawData = XLSX.utils.sheet_to_json<string[]>(sheet, { header: 1 })
            const headers = (rawData[0] || []).map((h) => String(h).trim())
            const rows = XLSX.utils.sheet_to_json<Record<string, any>>(sheet, { defval: "" })
            resolve({ headers, rows })
          }
        } catch {
          reject(new Error("Failed to parse file"))
        }
      }
      reader.onerror = () => reject(new Error("Failed to read file"))
      file.name.endsWith(".csv") ? reader.readAsText(file) : reader.readAsBinaryString(file)
    })
  }

  const validateFile = useCallback(
    async (selectedFile: File): Promise<boolean> => {
      const { headers, rows } = await parseFileData(selectedFile)
      const lang = detectLanguage(headers)

      if (!lang) {
        const missing = isHebrew
          ? `${Object.values(REQUIRED_HEADERS).join(", ")} :או ${Object.keys(REQUIRED_HEADERS).join(", ")}`
          : `${Object.keys(REQUIRED_HEADERS).join(", ")} or ${Object.values(REQUIRED_HEADERS).join(", ")}`
        showError(`${translate("Missing required headers")}:\n${missing}`)
        return false
      }

      const validStatuses = (taskStatus || [])
        .filter((s) => s.for_task)
        .map((s) => s.name.toLowerCase().trim())
      const validPriorities = (taskPrioritiesList || []).map((p) => ({
        name: p.name.toLowerCase().trim(),
        id: String(p.id),
      }))

      const getHeader = (key: string) => headers.find((h) => getHeaderKey(h, lang) === key)
      const titleH = getHeader("title"),
        startH = getHeader("start"),
        finishH = getHeader("finish")
      const statusH = getHeader("status"),
        priorityH = getHeader("priority")

      for (let i = 0; i < rows.length; i++) {
        const row = rows[i]
        const rowNum = i + 2

        if (titleH && !String(row[titleH] ?? "").trim()) {
          showError(`${translate("Row")} ${rowNum}: ${translate("Empty title")}`)
          return false
        }

        if (startH && row[startH] && !isValidTimestamp(row[startH])) {
          showError(`${translate("Row")} ${rowNum}: ${translate("Invalid start date")}`)
          return false
        }

        if (finishH && row[finishH] && !isValidTimestamp(row[finishH])) {
          showError(`${translate("Row")} ${rowNum}: ${translate("Invalid finish date")}`)
          return false
        }

        if (startH && finishH && row[startH] && row[finishH]) {
          const start = parseDate(row[startH]),
            finish = parseDate(row[finishH])
          if (start && finish && start > finish) {
            showError(`${translate("Row")} ${rowNum}: ${translate("Start date after finish date")}`)
            return false
          }
        }

        if (
          statusH &&
          row[statusH] &&
          !validStatuses.includes(String(row[statusH]).toLowerCase().trim())
        ) {
          const validNames = (taskStatus || [])
            .filter((s) => s.for_task)
            .map((s) => s.name)
            .join(", ")
          showError(
            `${translate("Row")} ${rowNum}: ${`${translate("Invalid status")}. ${translate("Allowed")}: ${validNames}`}`,
          )
          return false
        }

        if (priorityH && row[priorityH]) {
          const val = String(row[priorityH]).trim()
          if (!validPriorities.some((p) => p.name === val.toLowerCase() || p.id === val)) {
            const validNames = (taskPrioritiesList || [])
              .map((p) => `${p.name} (${p.id})`)
              .join(", ")
            showError(
              `${translate("Row")} ${rowNum}: ${`${translate("Invalid priority")}. ${translate("Allowed")}: ${validNames}`}`,
            )
            return false
          }
        }
      }

      setValidRowCount(rows.length)
      return true
    },
    [taskStatus, taskPrioritiesList, isHebrew, translate, showError],
  )

  const handleFileSelect = useCallback(
    async (selectedFile: File) => {
      setValidRowCount(null)
      if (!isValidFileType(selectedFile)) {
        showError(translate("Invalid file type. Please upload .xls, .xlsx, or .csv file"))
        return
      }

      setIsValidating(true)
      try {
        const isValid = await validateFile(selectedFile)
        setFile(isValid ? selectedFile : null)
      } catch {
        showError(translate("Failed to read file. Please check the file format"))
      } finally {
        setIsValidating(false)
      }
    },
    [validateFile, showError, translate],
  )

  const handleDrag = useCallback((e: React.DragEvent, dragging: boolean) => {
    e.preventDefault()
    e.stopPropagation()
    setIsDragging(dragging)
  }, [])

  const handleDrop = useCallback(
    (e: React.DragEvent) => {
      handleDrag(e, false)
      const droppedFile = e.dataTransfer.files[0]
      if (droppedFile) handleFileSelect(droppedFile)
    },
    [handleDrag, handleFileSelect],
  )

  const handleUpload = async () => {
    if (!file) return
    setIsLoading(true)
    try {
      const formData = new FormData()
      formData.append("tasks_file", file)
      formData.append("project_id", projectId)

      await axios.post(SERVER_URL + TASKS_IMPORT_TO_PROJECT, formData, {
        headers: { Authorization: "Bearer " + accessToken, "Content-Type": "multipart/form-data" },
      })

      onSuccess?.()
      toast({
        title: translate("Success"),
        description: translate("Task import successfully"),
        variant: "success",
      })
      handleClose()
    } catch (err: any) {
      showError(err.message || translate("Failed to upload file. Please try again"))
    } finally {
      setIsLoading(false)
    }
  }

  const handleRemoveFile = () => {
    setFile(null)
    setValidRowCount(null)
    if (fileInputRef.current) fileInputRef.current.value = ""
  }

  return (
    <Dialog open={importTaskDialog} onOpenChange={(open) => !open && handleClose()}>
      <DialogContent className={cn("sm:max-w-[500px]", isHebrew ? "rtl" : "ltr")}>
        <DialogHeader>
          <DialogTitle className={isHebrew ? "text-right" : "text-left"}>
            {translate("Import Tasks")}
          </DialogTitle>
          <DialogDescription className={isHebrew ? "text-right" : "text-left"}>
            {translate(
              "Import tasks from a CSV or Excel file to quickly add multiple tasks to your project",
            )}
          </DialogDescription>
        </DialogHeader>

        <div
          className={cn(
            "relative flex flex-col items-center justify-center rounded-lg border-2 border-dashed p-8 transition-colors",
            isDragging
              ? "border-primary bg-primary/5"
              : "border-muted-foreground/25 hover:border-muted-foreground/50",
            file && "border-solid border-green-500 bg-green-50",
          )}
          onDragEnter={(e) => handleDrag(e, true)}
          onDragLeave={(e) => handleDrag(e, false)}
          onDragOver={(e) => e.preventDefault()}
          onDrop={handleDrop}
        >
          <input
            ref={fileInputRef}
            type='file'
            accept='.xls,.xlsx,.csv'
            onChange={(e) => e.target.files?.[0] && handleFileSelect(e.target.files[0])}
            className='hidden'
          />

          {isValidating ? (
            <div className='flex flex-col items-center gap-2'>
              <div className='h-12 w-12 animate-spin rounded-full border-4 border-primary border-t-transparent' />
              <p className='text-sm text-muted-foreground'>{translate("Validating file...")}</p>
            </div>
          ) : file ? (
            <div className='flex flex-col items-center gap-2'>
              <div className='flex h-12 w-12 items-center justify-center rounded-full bg-green-100'>
                <Check className='h-6 w-6 text-green-600' />
              </div>
              <div className='text-center'>
                <p className='font-medium text-foreground'>{file.name}</p>
                <p className='text-sm text-muted-foreground'>{(file.size / 1024).toFixed(1)} KB</p>
                {validRowCount !== null && (
                  <p className='text-sm text-green-600'>
                    {translate("All")} {validRowCount} {translate("rows validated successfully")}
                  </p>
                )}
              </div>
              <Button
                type='button'
                variant='ghost'
                size='sm'
                onClick={handleRemoveFile}
                className='text-destructive hover:text-destructive'
              >
                {translate("Remove file")}
              </Button>
            </div>
          ) : (
            <div className='flex flex-col items-center gap-2 text-center'>
              <div className='flex h-12 w-12 items-center justify-center rounded-full bg-muted'>
                <Upload className='h-6 w-6 text-muted-foreground' />
              </div>
              <div>
                <p className='font-medium text-foreground'>
                  {translate("Drag and drop your file here")}
                </p>
                <p className='text-sm text-muted-foreground'>{translate("or")}</p>
              </div>
              <Button
                type='button'
                variant='outline'
                size='sm'
                onClick={() => fileInputRef.current?.click()}
              >
                {translate("Browse files")}
              </Button>
              <p className='text-xs text-muted-foreground'>
                {translate("Supported formats: XLS, XLSX, CSV")}
              </p>
            </div>
          )}
        </div>

        {/* Download Template */}
        <div className='flex items-center justify-center gap-2 text-sm text-muted-foreground'>
          <Download className='h-4 w-4' />
          <span>{translate("Download template")}:</span>
          <a
            href={TEMPLATE_URLS.en}
            download='import_tasks_en.xlsx'
            className='text-primary hover:underline'
          >
            English
          </a>
          <span>|</span>
          <a
            href={TEMPLATE_URLS.he}
            download='import_tasks_he.xlsx'
            className='text-primary hover:underline'
          >
            עברית
          </a>
        </div>

        <DialogFooter className='flex-col-reverse gap-2 !space-x-0 sm:flex-row sm:gap-0'>
          <DialogClose asChild className={isHebrew ? "!ml-2 sm:!ml-2" : "sm:mr-2"}>
            <Button type='button' variant='outline' disabled={isLoading || isValidating}>
              {translate("Cancel")}
            </Button>
          </DialogClose>
          <Button
            type='button'
            onClick={handleUpload}
            disabled={!file || isLoading || isValidating}
            loading={isLoading}
          >
            {translate("Upload")}
          </Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  )
}

export default ImportTaskDialog
